import { useCallback, useEffect, useState } from 'react';

import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useSiteUserPersonalization } from '@atlassian/jira-personalization-service/src/services/site-user/index.tsx';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';

import { fetchCachedUserCanRequestAccess } from './request-capabilities/index.tsx';
import { fetchCachedConfluenceAccess, setConfluenceAccessCache } from './product-access/index.tsx';

export const useConfluenceAvailabilityExperiment = (cloudId: string, isExpanded: boolean) => {
	const [isEligible, setIsEligible] = useState<boolean>(false);
	const [tabClicked, setTabClicked] = useState<boolean>(false);

	const licensedProducts = useLicensedProducts();
	const { user } = useCurrentUser().data;
	const { fetch } = useSiteUserPersonalization();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isAnonymousUser = useIsAnonymous();

	const setAllowConfluenceAccessCache = useCallback(() => {
		setConfluenceAccessCache(true, createAnalyticsEvent);
	}, [createAnalyticsEvent]);

	useEffect(() => {
		const checkEligibility = async () => {
			if (!isExpanded) {
				return;
			}

			if (fg('cc_virality_conf_availability_in_jira_search_kill')) {
				return;
			}

			if (isAnonymousUser) {
				return;
			}

			if (!licensedProducts?.confluence) {
				return;
			}

			const confluenceAccess = await fetchCachedConfluenceAccess(cloudId, createAnalyticsEvent);

			if (confluenceAccess) {
				return;
			}

			const isRaCapable = await fetchCachedUserCanRequestAccess(cloudId, createAnalyticsEvent);
			if (!isRaCapable) {
				return;
			}

			if (!expValEquals('cc_virality_conf_availability_in_jira_search', 'cohort', 'test')) {
				return;
			}

			if (user.accountId) {
				const personalizationData = await fetch({ cloudId, userId: user.accountId });
				if (personalizationData) {
					const isClicked =
						personalizationData.find(
							(trait) =>
								trait.name ===
									'jira-platform_screen_viewed_crossproductsearchconfluenceupselltab_changed' &&
								trait.value === true,
						) !== undefined;
					setTabClicked(isClicked);
				}
			}

			setIsEligible(true);
		};

		checkEligibility();
	}, [
		cloudId,
		licensedProducts,
		fetch,
		user.accountId,
		createAnalyticsEvent,
		isAnonymousUser,
		isExpanded,
	]);

	return { isEligible, tabClicked, setAllowConfluenceAccessCache };
};
